/* eslint-disable react/prop-types */
import React from 'react';
import type { ELSModalService } from '@els/els-component-modal-react';
import { ELSDropDownOption } from '@els/els-component-form-field-react';
import {
  RecommendationDto,
  RemediationActivityTypeDto
} from '../../apis/nhe-remediation-service/nhe-remediation-service.dtos';
import { AssessmentGoalDto } from '../../apis/eols-assessment-service/eols-assessment-service.dtos';
import RecommendationDetailTable from './RecommendationDetailTable.component';
import { FlexLayout } from '../../components/flex/FlexLayout.component';
import { FlexLayoutModifier } from '../../components/flex/flex.constants';
import { FlexItem } from '../../components/flex/FlexItem.component';
import {
  ELSDropDown,
  ELSIcon
} from '../../components/els.components';
import {
  ActivityTypeConfigMap,
  RemediationGoalType,
  RemediationContentTypeConfigMap
} from '../../models/remediation.models';
import { ActivityProgressApplyScoreFilterDisplayName } from './remediation-student-activity.constants';
import { ActivityProgressApplyScoreFilter } from './remediation-student-activity.models';
import { isAnyRecommendationScored } from './remediation-student-activity.utilities';
import {
  getGoalBreadcrumbs,
  sortRecommendations
} from '../../utilities/remediation.utilities';
import IconWithText from '../../components/icon-with-text/IconWithText.component';

export const GoalRecommendationModalId = 'GoalRecommendationModalId';

export type GoalRecommendationModalProps = {
  activityType: RemediationActivityTypeDto;
  selectedGoal: AssessmentGoalDto;
  categoryGroupName?: string | null;
  goalRecommendations: RecommendationDto[];
  modalService: ELSModalService;
};

export type GoalRecommendationModalState = {
  selectedApplyScoreFilter: ActivityProgressApplyScoreFilter;
}

const defaultState: GoalRecommendationModalState = {
  selectedApplyScoreFilter: ActivityProgressApplyScoreFilter.BEST_ATTEMPT
};

const GoalRecommendationModal = (props: GoalRecommendationModalProps): JSX.Element => {
  const [state, setState] = React.useState<GoalRecommendationModalState>(defaultState);

  const handleCancelClick = () => {
    props.modalService.closeModal(GoalRecommendationModalId);
  };

  const handleApplyScoreFilterChange = (value: ActivityProgressApplyScoreFilter) => {
    setState({
      selectedApplyScoreFilter: value
    });
  };

  const getDropDownOptions = (): ELSDropDownOption[] => {
    return Object.values(ActivityProgressApplyScoreFilter)
      .map((option) => {
        return {
          name: ActivityProgressApplyScoreFilterDisplayName[option],
          value: option
        };
      });
  };

  const getRecommendationsForSelectedGoal = (): RecommendationDto[] => {
    const goals = props.goalRecommendations.filter((recommendation) => {
      return recommendation.assessmentGoalVtwId === props.selectedGoal.vtwId;
    });
    return sortRecommendations(goals, [props.selectedGoal]);
  };

  const getDetailsLanguageForGoal = (): string => {
    const recommendations = getRecommendationsForSelectedGoal();
    const { goal } = props.selectedGoal;
    const { goalType } = ActivityTypeConfigMap[props.activityType];

    const recsEqualGoals = recommendations.length === goal;

    const contentTypeDisplayName = goalType === RemediationGoalType.CONTENT_TYPE && recommendations.length > 0
      ? RemediationContentTypeConfigMap[recommendations[0].contentType].titlePlural.toLowerCase()
      : '';

    if (goalType === RemediationGoalType.TAXON) {
      if (recsEqualGoals) {
        return 'Below are the activities this student was assigned for this topic.';
      } else {
        return 'Below are the activity choices this student was given for this topic.';
      }
    } else {
      if (recsEqualGoals) {
        return `Below are the activities this student was assigned for ${contentTypeDisplayName}.`;
      } else {
        return `Below are the activity choices this student was given for ${contentTypeDisplayName}.`;
      }
    }
  };

  const goalBreadcrumbs = getGoalBreadcrumbs(props.selectedGoal, props.categoryGroupName)

  return (
    <div>
      <div className="c-els-modal">
        <div className="c-els-modal__window u-els-width-1o1@mobile u-els-width-3o4">
          <div className="u-els-margin-bottom">
            <FlexLayout
              modifiers={[FlexLayoutModifier.SPACE_BETWEEN, FlexLayoutModifier.MIDDLE]}
            >
              <FlexItem modifiers={[FlexLayoutModifier.GROW]}>
                <h2><strong>{ActivityTypeConfigMap[props.selectedGoal.type].title}</strong> / {goalBreadcrumbs.text}</h2>
              </FlexItem>

              <FlexItem>
                <button
                  type="button"
                  className="u-els-anchorize u-els-debuttonize"
                  onClick={() => handleCancelClick()}
                >
                  <ELSIcon name="close" size="1x" />
                  <span className="u-els-hide-visually">Close</span>
                </button>
              </FlexItem>
            </FlexLayout>
          </div>

          <div className="c-els-divider o-els-container" />

          <FlexLayout modifiers={[FlexLayoutModifier.LEFT, FlexLayoutModifier.SPACE_BETWEEN, FlexLayoutModifier.TOP]}>
            <FlexItem>
              <table className="c-nra-goal-table">
                <tbody>
                  {
                      goalBreadcrumbs.path && goalBreadcrumbs.path.length > 0 && (
                          <tr>
                            <td className="u-els-nowrap">
                              <IconWithText iconName="bullseye" iconPrefix="gizmo">Subject Area</IconWithText>
                            </td>
                            <td>
                              {goalBreadcrumbs.pathDisplay}
                            </td>
                          </tr>
                      )
                  }
                  <tr>
                    <td>
                      <IconWithText iconName="syllabus" iconPrefix="hmds">Details</IconWithText>
                    </td>
                    <td>
                      {getDetailsLanguageForGoal()}
                    </td>
                  </tr>
                </tbody>
              </table>
            </FlexItem>
            <FlexItem
                classes={['u-els-margin-top-1o2 u-els-margin-bottom']}
                isRender={isAnyRecommendationScored(props.goalRecommendations)}
            >
              <ELSDropDown
                  value={state.selectedApplyScoreFilter}
                  options={getDropDownOptions()}
                  changeHandler={(e, value) => handleApplyScoreFilterChange(value)}
              >
                Show score of
              </ELSDropDown>
            </FlexItem>
          </FlexLayout>

          <RecommendationDetailTable
            activityType={props.activityType}
            selectedApplyScoreFilter={state.selectedApplyScoreFilter}
            goalRecommendations={getRecommendationsForSelectedGoal()}
          />
        </div>
      </div>
    </div>
  );
};

export default GoalRecommendationModal;
